import {
	type LinksFunction,
	type LoaderFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	json,
} from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { getMDXComponent } from "mdx-bundler/client";
import { useMemo } from "react";
import { BlogTimeline } from "~/components/BlogTimeline/BlogTimeline";
import { type Frontmatter, getPost } from "~/utils/blog_post.server";

export const loader: LoaderFunction = async ({
	params,
}: LoaderFunctionArgs) => {
	const slug = params["*"];
	if (!slug) throw new Response("Not found", { status: 404 });

	const post = await getPost(slug);
	if (post) {
		const { frontmatter, code } = post;
		return json({ frontmatter, code });
	}
	throw new Response("Not found", { status: 404 });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const frontmatter = data?.frontmatter as Frontmatter;
	return [
		{ title: frontmatter.title || "Blog" },
		{ description: frontmatter.description || "Blog" },
		{ "og:twitter:title": frontmatter.title || "Blog" },
		{ "og:twitter:description": frontmatter.description || "Blog" },
	];
};

export const links: LinksFunction = () => [
	{
		rel: "stylesheet",
		href: "https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.8.0/styles/github-dark.min.css",
	},
];

interface LoaderData {
	code: string;
	frontmatter: Frontmatter;
}

export function PostHeader({ frontmatter }: { frontmatter: Frontmatter }) {
	return (
		<div>
			<h1>{frontmatter.title}</h1>
			<small>
				<time>{frontmatter.date}</time> -{" "}
				<span>{frontmatter.readingTime.text}</span>
			</small>
		</div>
	);
}

export default function Post() {
	const { code, frontmatter } = useLoaderData<LoaderData>();
	const Component = useMemo(() => getMDXComponent(code), [code]);

	return (
		<div className="prose prose-sm mx-auto">
			<PostHeader frontmatter={frontmatter} />
			<Component />
		</div>
	);
}
